import { deriveHasExpiration } from '@geartracker/utils';

import { createZodDto } from './zod';

import { z } from 'zod';

// PostgreSQL timestamp max is year 294276, but JS ISO 8601 extended years ("+010000-...")
// cannot be stored. Cap at year 9999 to match standard date formatting.
const MAX_SAFE_DATE = new Date('9999-12-31T23:59:59.999Z');
const safeCoerceDate = z.coerce.date().max(MAX_SAFE_DATE);

// schema for existing inspections
const InspectionSchema = z.object({
  id: z.number().nullable(),
  date: safeCoerceDate,
  //.max(add(new Date(new Date().toISOString()), { hours: 1 })), //necessary to compare dates in Zulu format, add one hours to avoid date comparison error when setting today's date in frontend // REMOVED DUE TO BUG
  status: z.enum(['IN_STOCK', 'DAMAGED', 'QUARANTINE', 'LOST', 'TRASH']),
  comment: z.string().nullable(),
  userId: z.number().nullable(),
});

// schema for allocating new equipment to user
export const AllocateEquipmentSchema = z.object({
  userId: z.number().nullable(),
});

export const upsertEquipmentSchemaBase = z.object({
  name: z.string(),
  description: z.string().nullable(),
  serialNumber: z.string().nullable(),
  partNumber: z.string().nullable(),
  nfcTags: z.string().array(),
  price: z.number().safe().nullable(),
  shareableId: z.string().nullable(),
  manufactureDate:
    safeCoerceDate //As we do not check date comparison here, we can have a manufacture date later than commissioning date, but we had a bug below so i let it be.
      .nullable(),
  commissioningDate: safeCoerceDate.nullable(),
  inspectionPeriod: z.number().positive().nullable(),
  manufactureLifeSpan: z.number().min(0).nullable(),
  commissioningLifeSpan: z.number().min(0).nullable(),
  // Legacy clients may still send hasExpiration, but the backend derives it
  // from the configured lifespans to avoid trusting client-side business logic.
  hasExpiration: z.boolean().optional(),
  brandId: z.number().nullable(),
  modelId: z.number().nullable(),
  categoryId: z.number().nullable(),
  tagIds: z.number().array(),
  // Legacy mobile/web clients can still send base64 images, but the backend no
  // longer persists them. Images should be uploaded through the R2 image flow.
  imageBase64: z.string().nullable().optional(),
  imageSmallBase64: z.string().nullable().optional(),
  inspections: InspectionSchema.array(),
  userId: z.number().nullable(),
  folderId: z.number().nullable(),
  filesIds: z.number().array(),
  imagesIds: z.number().array(),
  inspectionChecklistId: z.number().nullable().optional(),
});

const upsertEquipmentSchema = upsertEquipmentSchemaBase.transform((data) => ({
  ...data,
  hasExpiration: deriveHasExpiration(data),
}));

// We need to enforce the folderId to be present when updating an equipment compared to creating one
const CreateEquipmentSchema = upsertEquipmentSchema;

const UpdateEquipmentSchema = upsertEquipmentSchemaBase
  .omit({
    folderId: true,
  })
  .extend({
    folderId: z.number(),
  })
  .transform((data) => ({
    ...data,
    hasExpiration: deriveHasExpiration(data),
  }));

const ChecklistAnswerSchema = z.object({
  checklistItemId: z.number(),
  value: z.enum(['PASS', 'FAIL', 'NA']).nullable().optional(),
  numericValue: z.number().nullable().optional(),
  textValue: z.string().nullable().optional(),
  comment: z.string().nullable().optional(),
});

// Repair v3: an inspection now carries a standardised `result` plus an optional
// `followUpAction`. The backend derives the equipment HardwareStatus and any
// triggered Repair from this pair (see inspectionResultToHardwareStatus):
//   CONFORM              → NONE                          → IN_STOCK
//   CONFORM_WITH_RESERVE → NONE | OPEN_REPAIR(non-block) → IN_STOCK | DAMAGED
//   FAIL                 → OPEN_REPAIR(blocking) | TRASH → QUARANTINE | TRASH
//   LOST (not inspected) → TRASH | OPEN_REPAIR(non-block) | LOST
//                           (no EquipmentInspection is ever recorded for LOST)
const inspectionResultFields = {
  comment: z.string().nullable(),
  result: z.enum(['CONFORM', 'CONFORM_WITH_RESERVE', 'FAIL', 'LOST']),
  followUpAction: z
    .enum(['NONE', 'OPEN_REPAIR', 'TRASH', 'LOST'])
    .default('NONE'),
  inspectingUserId: z.number(),
  checklistAnswers: z
    .object({
      checklistId: z.number(),
      answers: ChecklistAnswerSchema.array(),
    })
    .nullable()
    .optional(),
} as const;

const ALLOWED_FOLLOW_UPS: Record<string, string[]> = {
  CONFORM: ['NONE'],
  CONFORM_WITH_RESERVE: ['NONE', 'OPEN_REPAIR'],
  FAIL: ['OPEN_REPAIR', 'TRASH'],
  LOST: ['TRASH', 'OPEN_REPAIR', 'LOST'],
};

const refineFollowUpAction = (
  data: { result: string; followUpAction?: string },
  ctx: z.RefinementCtx,
) => {
  const followUp = data.followUpAction ?? 'NONE';
  if (!ALLOWED_FOLLOW_UPS[data.result]?.includes(followUp)) {
    ctx.addIssue({
      code: 'custom',
      path: ['followUpAction'],
      message: `Follow-up action "${followUp}" is not allowed for result "${data.result}"`,
    });
  }
};

export const InspectEquipmentSchema = z
  .object({
    ...inspectionResultFields,
    // An inspection can complete selected currently-open repairs in the same
    // transaction. This is deliberately an explicit list, never an implicit
    // "close all", so the user's confirmation remains auditable.
    closeOpenRepairIds: z.array(z.number().int().positive()).default([]),
    // Photos taken during the inspection, uploaded ahead of time via POST
    // /images and only linked to the EquipmentInspection here. Single-equipment
    // inspections only — bulk inspections never carry photos.
    imagesIds: z.array(z.number().int().positive()).default([]),
  })
  .superRefine((data, ctx) => {
    refineFollowUpAction(data, ctx);
    if (
      data.closeOpenRepairIds.length > 0 &&
      data.result !== 'CONFORM' &&
      data.result !== 'CONFORM_WITH_RESERVE'
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['closeOpenRepairIds'],
        message:
          'Open repairs can only be completed from a conform inspection result',
      });
    }
  });

// schema for inspecting multiple equipments
export const InspectEquipmentsSchema = z
  .object({
    ids: z.number().array(),
    ...inspectionResultFields,
  })
  .superRefine(refineFollowUpAction);

const bulkEquipmentUpdateSchema = z
  .object({
    equipmentIds: z.number().array().min(1),
    tagIdsToAdd: z.number().array(),
    tagIdsToRemove: z.number().array(),
    // Preserve `null` as a no-op for legacy clients. New clients use
    // `unassignUser` to make an unassignment explicit.
    userId: z.number().nullable().optional(),
    unassignUser: z.boolean().optional(),
    folderId: z.number().nullable(),
    inspectionChecklistId: z.number().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    // At least one field must be set
    const hasTagsToAdd = data.tagIdsToAdd?.length ?? 0;
    const hasTagsToRemove = data.tagIdsToRemove?.length ?? 0;

    if (
      data.userId == null &&
      !data.unassignUser &&
      !data.folderId &&
      !data.inspectionChecklistId &&
      hasTagsToAdd === 0 &&
      hasTagsToRemove === 0
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one of the edit fields must be set',
      });
    }

    if (data.unassignUser && data.userId != null) {
      ctx.addIssue({
        code: 'custom',
        path: ['unassignUser'],
        message: 'Cannot assign and unassign a user in the same bulk update',
      });
    }
  })
  .transform((data) => {
    // Coerce nulls to empty arrays for safe filtering
    const tagIdsToAdd = data.tagIdsToAdd ?? [];
    const tagIdsToRemove = data.tagIdsToRemove ?? [];

    const intersection = tagIdsToAdd.filter((tagId) =>
      tagIdsToRemove.includes(tagId),
    );
    if (intersection.length === 0) {
      return { ...data, tagIdsToAdd, tagIdsToRemove };
    }

    // If there are intersecting tag IDs, remove them from both arrays
    // as they cancel each other out
    return {
      ...data,
      tagIdsToAdd: tagIdsToAdd.filter((id) => !intersection.includes(id)),
      tagIdsToRemove: tagIdsToRemove.filter((id) => !intersection.includes(id)),
    };
  });

export class BulkEquipmentUpdateDto extends createZodDto(
  bulkEquipmentUpdateSchema,
) {}

const EquipmentIdsSchema = z.number().array();

const csvNumberArray = z
  .union([z.string(), z.array(z.union([z.string(), z.number()])), z.number()])
  .optional()
  .transform((value) => {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    const rawValues = Array.isArray(value) ? value : String(value).split(',');
    const numbers = rawValues
      .map((item) => Number(String(item).trim()))
      .filter((item) => Number.isInteger(item) && item > 0);

    return numbers.length ? numbers : undefined;
  });

const csvStringArray = <T extends [string, ...string[]]>(values: T) =>
  z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) => {
      if (value === undefined || value === null || value === '') {
        return undefined;
      }

      const allowed = new Set(values);
      const rawValues = Array.isArray(value) ? value : value.split(',');
      const result = rawValues
        .map((item) => item.trim())
        .filter((item): item is T[number] => allowed.has(item));

      return result.length ? result : undefined;
    });

const optionalBoolean = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform((value) => {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }
    return value === true || value === 'true';
  });

const optionalPositiveInt = (defaultValue: number, max: number) =>
  z
    .union([z.number(), z.string()])
    .optional()
    .transform((value) => {
      const raw = value === undefined || value === '' ? defaultValue : value;
      const parsed = Number(raw);

      if (!Number.isFinite(parsed)) {
        return defaultValue;
      }

      return Math.min(Math.max(Math.trunc(parsed), 1), max);
    });

const optionalPage = z
  .union([z.number(), z.string()])
  .optional()
  .transform((value) => {
    const raw = value === undefined || value === '' ? 0 : value;
    const parsed = Number(raw);

    if (!Number.isFinite(parsed)) {
      return 0;
    }

    return Math.max(Math.trunc(parsed), 0);
  });

const EquipmentListQuerySchema = z.object({
  page: optionalPage,
  take: optionalPositiveInt(50, 100),
  folderIds: csvNumberArray,
  userIds: csvNumberArray,
  tagIds: csvNumberArray,
  equipmentIds: csvNumberArray,
  tagMode: z.enum(['AND', 'OR']).optional().default('AND'),
  generalStatuses: csvStringArray([
    'IN_STOCK',
    'CONFORM',
    'NOT_CONFORM',
    'NOTIFY',
    'DAMAGED',
    'QUARANTINE',
    'LOST',
    'TRASH',
  ]),
  deadline: z.enum(['INSPECT_60D', 'REPLACE_60D']).nullable().optional(),
  search: z
    .string()
    .optional()
    .transform((value) => {
      const trimmed = value?.trim();
      return trimmed || undefined;
    }),
  sortField: z
    .enum([
      'name',
      'generalStatus',
      'nextInspectionDate',
      'expirationDate',
      'lastInspectionDate',
      'manufactureDate',
      'commissioningDate',
      'createdAt',
      'updatedAt',
      'serialNumber',
      'partNumber',
      'price',
      'hasExpiration',
      'Brand',
      'Model',
      'Folder',
      'user',
    ])
    .optional(),
  sortDirection: z.enum(['asc', 'desc']).optional().default('asc'),
  includeFacets: optionalBoolean.default(false),
  includeData: optionalBoolean.default(true),
});

const EquipmentLookupQuerySchema = z.object({
  search: z.string().trim().min(1),
});

export class CreateEquipmentDto extends createZodDto(CreateEquipmentSchema) {}

export class UpdateEquipmentDto extends createZodDto(UpdateEquipmentSchema) {}

export class InspectEquipmentDto extends createZodDto(InspectEquipmentSchema) {}

export class InspectEquipmentsDto extends createZodDto(
  InspectEquipmentsSchema,
) {}

export class EquipmentIdsDto extends createZodDto(EquipmentIdsSchema) {}

export class AllocateEquipmentDto extends createZodDto(
  AllocateEquipmentSchema,
) {}

export class EquipmentListQueryDto extends createZodDto(
  EquipmentListQuerySchema,
) {}

export class EquipmentLookupQueryDto extends createZodDto(
  EquipmentLookupQuerySchema,
) {}
